import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Gift, Check, Loader2, AlertCircle, Edit2, CreditCard, Tag } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import type { Business } from '../../contexts/BusinessContext';
import { GiftCardSettings } from '../../services/giftCardSettingsService';
import GiftCardWrapper from '../../components/GiftCardWrapper';
import { loadStripe } from '@stripe/stripe-js';
import { GiftCardPromotionService, PromotionBonus } from '../../services/giftCardPromotionService';

export default function BuyGiftCard() {
  const { businessSlug } = useParams<{ businessSlug: string }>();
  const navigate = useNavigate();
  
  // Debug: Log component mount
  useEffect(() => {
    console.log('🎁 BuyGiftCard component mounted', {
      businessSlug,
      userAgent: navigator.userAgent,
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight
    });
  }, []);
  
  const [business, setBusiness] = useState<Business | null>(null);
  const [designs, setDesigns] = useState<GiftCardSettings[]>([]);
  const [selectedDesign, setSelectedDesign] = useState<GiftCardSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState<'design' | 'amount' | 'purchaser' | 'confirmation'>('design');
  const [stripeActive, setStripeActive] = useState(false);
  const [processingCheckout, setProcessingCheckout] = useState(false);
  const [searchParams] = useSearchParams();
  
  // Gift card amount state
  const [amount, setAmount] = useState('');
  
  // Promotion state
  const [promotionBonus, setPromotionBonus] = useState<PromotionBonus | null>(null);
  
  // Purchaser information state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  
  // Recipient information state
  const [showRecipient, setShowRecipient] = useState(false);
  const [recipientName, setRecipientName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientMessage, setRecipientMessage] = useState('');
  
  // Edit mode state for confirmation screen
  const [editingAmount, setEditingAmount] = useState(false);
  const [editingPurchaser, setEditingPurchaser] = useState(false);
  const [editingRecipient, setEditingRecipient] = useState(false);

  const handleCheckoutSuccess = (sessionId: string) => {
    // Redirect to success page - the webhook will create the gift card
    // The success page will load the gift card data
    if (businessSlug) {
      navigate(`/${businessSlug}/gift-card-success?session_id=${sessionId}`);
    } else {
      console.error('Cannot redirect: businessSlug is missing');
      setError('Unable to complete checkout. Please contact support.');
    }
  };

  const handleCheckout = async () => {
    if (!business || !selectedDesign || !amount || !firstName || !lastName || !email || !phone) {
      return;
    }

    if (!stripeActive) {
      setError('Stripe integration is not active. Please contact the business.');
      return;
    }

    try {
      setProcessingCheckout(true);
      const amountNum = parseFloat(amount);
      const amountInCents = Math.round(amountNum * 100);

      // Create Stripe checkout session
      const { data, error } = await supabase.functions.invoke('create-platform-payment', {
        body: {
          businessId: business.id,
          totalAmount: amountInCents,
          platformFeePercentage: 1.0,
          successUrl: `${window.location.origin}/${businessSlug}/gift-card-success?session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${window.location.origin}/${businessSlug}/gift-cards?canceled=true`,
          description: `Gift Card - ${business.name}`,
          customerEmail: email,
          customerName: `${firstName} ${lastName}`,
          metadata: {
            gift_card_design_id: selectedDesign.id,
            gift_card_amount: amountNum.toString(),
            purchaser_first_name: firstName,
            purchaser_last_name: lastName,
            purchaser_email: email,
            purchaser_phone: phone,
            recipient_name: recipientName || '',
            recipient_email: recipientEmail || '',
            recipient_message: recipientMessage || '',
            promotion_id: promotionBonus?.promotion.id || '',
            bonus_amount: promotionBonus?.bonusAmount.toString() || '0'
          }
        }
      });

      if (error) {
        throw error;
      }

      if (!data?.checkoutUrl && !data?.sessionId) {
        throw new Error('No checkout URL or session ID returned');
      }

      // For Stripe Connect, redirect directly to checkoutUrl
      // For regular Stripe, use redirectToCheckout with sessionId
      if (data.checkoutUrl) {
        // Direct redirect for Connect accounts
        window.location.href = data.checkoutUrl;
      } else if (data.sessionId) {
        // Use Stripe.js redirect for regular accounts
        const stripe = await loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '');
        if (!stripe) {
          throw new Error('Stripe failed to load');
        }

        const { error: redirectError } = await stripe.redirectToCheckout({
          sessionId: data.sessionId
        });

        if (redirectError) {
          throw redirectError;
        }
      }
    } catch (err: any) {
      console.error('Checkout error:', err);
      setError(err.message || 'Failed to process checkout. Please try again.');
      setProcessingCheckout(false);
    }
  };

  // Load business and designs
  useEffect(() => {
    console.log('🎁 BuyGiftCard component mounted/updated', { businessSlug, hasSupabase: !!supabase });
    
    if (!businessSlug) {
      console.error('❌ No business slug provided');
      setError('Business not found');
      setLoading(false);
      return;
    }

    if (!supabase) {
      console.error('❌ Supabase public client not available');
      setError('Unable to connect to the database. Please refresh the page or contact support.');
      setLoading(false);
      return;
    }

    async function loadData() {
      try {
        console.log('🔄 Loading gift card page data for business slug:', businessSlug);
        
        // Load business
        const { data: businessData, error: businessError } = await supabase
          .from('businesses')
          .select('id, name, slug, phone, address, booking_settings, stripe_connect_account_id, stripe_connect_charges_enabled')
          .eq('slug', businessSlug)
          .single();

        console.log('📊 Business query result:', { businessData, businessError });

        if (businessError) {
          console.error('❌ Business query error:', businessError);
          setError(`Business not found: ${businessError.message}`);
          setLoading(false);
          return;
        }

        if (!businessData) {
          console.error('❌ No business data returned');
          setError('Business not found');
          setLoading(false);
          return;
        }

        setBusiness(businessData as Business);

        // Check Stripe status
        const hasStripe = !!(businessData.stripe_connect_account_id && businessData.stripe_connect_charges_enabled);
        setStripeActive(hasStripe);

        // Load gift card designs
        const { data: designsData, error: designsError } = await supabase
          .from('gift_card_settings')
          .select('*')
          .eq('business_id', businessData.id)
          .order('created_at', { ascending: false });

        if (designsError) {
          console.error('Error loading designs:', designsError);
          setError('Failed to load gift card designs');
          setLoading(false);
          return;
        }

        setDesigns(designsData || []);
        
        // Auto-select first design if available
        if (designsData && designsData.length > 0) {
          setSelectedDesign(designsData[0]);
        }

        // Check if returning from Stripe checkout (should redirect to success page)
        const sessionId = searchParams.get('session_id');
        if (sessionId) {
          handleCheckoutSuccess(sessionId);
          return; // Exit early, will redirect
        }
        
        // Check if checkout was canceled
        const canceled = searchParams.get('canceled');
        if (canceled === 'true') {
          setError('Checkout was canceled. Please try again if you would like to complete your purchase.');
        }

        console.log('✅ Gift card page data loaded successfully');
        setLoading(false);
      } catch (err) {
        console.error('❌ Error loading gift card page data:', err);
        setError(`Failed to load page: ${err instanceof Error ? err.message : 'Unknown error'}`);
        setLoading(false);
      }
    }

    loadData();
  }, [businessSlug, searchParams]);

  // Check for active promotions when amount changes
  useEffect(() => {
    const checkPromotion = async () => {
      if (!business?.id || !amount) {
        setPromotionBonus(null);
        return;
      }

      const amountNum = parseFloat(amount);
      if (isNaN(amountNum) || amountNum <= 0) {
        setPromotionBonus(null);
        return;
      }

      try {
        const bonus = await GiftCardPromotionService.applyPromotion(business.id, amountNum);
        setPromotionBonus(bonus);
      } catch (error) {
        console.error('Error checking promotion:', error);
        setPromotionBonus(null);
      }
    };

    checkPromotion();
  }, [amount, business?.id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading gift cards...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Error</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  // If no business but not loading and no error, show a helpful message
  if (!business && !loading && !error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <AlertCircle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Business Not Found</h1>
          <p className="text-gray-600 mb-6">
            We couldn't find the business you're looking for. Please check the URL and try again.
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-colors"
            style={{
              minHeight: '44px',
              touchAction: 'manipulation',
              WebkitTapHighlightColor: 'transparent'
            }}
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  // Still loading or no business yet
  if (!business) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading gift cards...</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen bg-gray-50"
      style={{
        WebkitTouchCallout: 'none',
        WebkitUserSelect: 'none',
        touchAction: 'manipulation',
        WebkitTapHighlightColor: 'transparent',
        minHeight: '100dvh', // Dynamic viewport height for mobile
        width: '100%',
        position: 'relative',
        zIndex: 1,
        display: 'block',
        visibility: 'visible',
        opacity: 1
      }}
    >
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {business.booking_settings?.logo_url && (
                <img
                  src={business.booking_settings.logo_url}
                  alt={business.name}
                  className="h-12 w-auto"
                />
              )}
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{business.name}</h1>
                <p className="text-gray-600">Gift Cards</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Page Header */}
        <div className="text-center mb-12">
          {currentStep === 'design' ? (
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Choose Your Gift Card Design
            </h2>
          ) : currentStep === 'amount' ? (
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Set Gift Card Amount
            </h2>
          ) : currentStep === 'purchaser' ? (
            <>
              <h2 className="text-4xl font-bold text-gray-900 mb-2">
                Add your information
              </h2>
              <p className="text-xl text-gray-600">
                Add a recipients information if different than yours
              </p>
            </>
          ) : (
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Review Your Order
            </h2>
          )}
        </div>

        {currentStep === 'design' && (
          <>
            {/* Design Selection */}
            {designs.length === 0 ? (
              <div className="bg-white rounded-lg shadow-lg p-12 text-center">
                <Gift className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  No Gift Card Designs Available
                </h3>
                <p className="text-gray-600">
                  This business hasn't set up any gift card designs yet.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {designs.map((design) => (
                  <div
                    key={design.id}
                    onClick={() => setSelectedDesign(design)}
                    className={`relative bg-white rounded-xl shadow-lg overflow-hidden cursor-pointer transition-all transform ${
                      selectedDesign?.id === design.id
                        ? 'ring-4 ring-blue-500 scale-105'
                        : 'hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]'
                    }`}
                    style={{
                      touchAction: 'manipulation',
                      WebkitTapHighlightColor: 'transparent'
                    }}
                  >
                    {/* Selected Indicator */}
                    {selectedDesign?.id === design.id && (
                      <div className="absolute top-4 right-4 z-10 bg-blue-600 text-white rounded-full p-2 shadow-lg">
                        <Check className="h-5 w-5" />
                      </div>
                    )}

                    {/* Gift Card Preview */}
                    <div className="p-6 bg-gray-100">
                      <GiftCardWrapper
                        settings={design}
                        businessLogoUrl={business.booking_settings?.logo_url}
                        showSampleData={true}
                        maxWidth="100%"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Continue Button */}
            {selectedDesign && designs.length > 0 && (
              <div className="mt-12 text-center">
                <button
                  onClick={() => setCurrentStep('amount')}
                  className="px-8 py-4 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white rounded-lg font-semibold text-lg transition-colors"
                  style={{
                    minHeight: '44px',
                    touchAction: 'manipulation',
                    WebkitTapHighlightColor: 'transparent'
                  }}
                >
                  Continue
                </button>
              </div>
            )}
          </>
        )}

        {currentStep === 'amount' && (
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-lg shadow-lg p-8">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (amount && parseFloat(amount) > 0) {
                    setCurrentStep('purchaser');
                  }
                }}
                className="space-y-6"
              >
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Gift Card Amount *
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 text-lg font-medium">
                      $
                    </span>
                    <input
                      type="tel"
                      inputMode="decimal"
                      pattern="[0-9]*"
                      value={amount}
                      onChange={(e) => {
                        // Only allow numbers and one decimal point
                        const value = e.target.value.replace(/[^0-9.]/g, '');
                        // Ensure only one decimal point
                        const parts = value.split('.');
                        const filteredValue = parts.length > 2 
                          ? parts[0] + '.' + parts.slice(1).join('')
                          : value;
                        setAmount(filteredValue);
                      }}
                      required
                      min="0.01"
                      step="0.01"
                      className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
                      placeholder="0.00"
                      style={{ 
                        WebkitAppearance: 'none',
                        MozAppearance: 'textfield',
                        touchAction: 'manipulation'
                      }}
                    />
                  </div>
                  <p className="text-sm text-gray-500 mt-2">
                    Enter the amount for this gift card
                  </p>

                  {/* Active Promotion Display */}
                  {promotionBonus && (
                    <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0">
                          <Tag className="h-5 w-5 text-green-600 mt-0.5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-semibold text-green-900">
                              {promotionBonus.promotion.name}
                            </span>
                            <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                              ACTIVE
                            </span>
                          </div>
                          {promotionBonus.promotion.description && (
                            <p className="text-xs text-green-700 mb-2">{promotionBonus.promotion.description}</p>
                          )}
                          <div className="flex items-center justify-between text-sm">
                            <div>
                              <div className="text-gray-600">Purchase Amount:</div>
                              <div className="text-gray-600">Bonus Amount:</div>
                              <div className="font-semibold text-green-900 border-t border-green-200 mt-1 pt-1">
                                Total Card Value:
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-gray-900">${parseFloat(amount).toFixed(2)}</div>
                              <div className="text-green-600 font-medium">+${promotionBonus.bonusAmount.toFixed(2)}</div>
                              <div className="font-bold text-green-900 border-t border-green-200 mt-1 pt-1">
                                ${promotionBonus.totalAmount.toFixed(2)}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setCurrentStep('design')}
                    className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 active:bg-gray-100 font-medium transition-colors"
                    style={{
                      minHeight: '44px',
                      touchAction: 'manipulation',
                      WebkitTapHighlightColor: 'transparent'
                    }}
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={!amount || parseFloat(amount) <= 0}
                    className="px-8 py-3 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white rounded-lg font-semibold transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                    style={{
                      minHeight: '44px',
                      touchAction: 'manipulation',
                      WebkitTapHighlightColor: 'transparent'
                    }}
                  >
                    Continue
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {currentStep === 'purchaser' && (
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-lg shadow-lg p-8">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  setCurrentStep('confirmation');
                }}
                className="space-y-6"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter first name"
                      style={{
                        minHeight: '44px',
                        WebkitAppearance: 'none',
                        touchAction: 'manipulation'
                      }}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter last name"
                      style={{
                        minHeight: '44px',
                        WebkitAppearance: 'none',
                        touchAction: 'manipulation'
                      }}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter email address"
                    style={{
                      minHeight: '44px',
                      WebkitAppearance: 'none',
                      touchAction: 'manipulation'
                    }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter phone number"
                    style={{
                      minHeight: '44px',
                      WebkitAppearance: 'none',
                      touchAction: 'manipulation'
                    }}
                  />
                </div>

                {/* Add Recipient Section */}
                <div className="pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setShowRecipient(!showRecipient)}
                    className="w-full flex items-center justify-between px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 active:bg-gray-100 transition-colors"
                    style={{
                      minHeight: '44px',
                      touchAction: 'manipulation',
                      WebkitTapHighlightColor: 'transparent'
                    }}
                  >
                    <span className="text-sm font-medium text-gray-700">
                      Add Recipient
                    </span>
                    <span className="text-sm text-gray-500">
                      {showRecipient ? 'Hide' : 'Show'}
                    </span>
                  </button>

                  {showRecipient && (
                    <div className="mt-6 space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Recipient Name
                        </label>
                        <input
                          type="text"
                          value={recipientName}
                          onChange={(e) => setRecipientName(e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Enter recipient name"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Recipient Email
                        </label>
                        <input
                          type="email"
                          value={recipientEmail}
                          onChange={(e) => setRecipientEmail(e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Enter recipient email"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Custom Message
                        </label>
                        <textarea
                          value={recipientMessage}
                          onChange={(e) => setRecipientMessage(e.target.value)}
                          rows={3}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Enter a custom message for the recipient (optional)"
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setCurrentStep('amount')}
                    className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 active:bg-gray-100 font-medium transition-colors"
                    style={{
                      minHeight: '44px',
                      touchAction: 'manipulation',
                      WebkitTapHighlightColor: 'transparent'
                    }}
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    className="px-8 py-3 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white rounded-lg font-semibold transition-colors"
                    style={{
                      minHeight: '44px',
                      touchAction: 'manipulation',
                      WebkitTapHighlightColor: 'transparent'
                    }}
                  >
                    Continue
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {currentStep === 'confirmation' && (
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-lg shadow-lg p-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Left Column - Gift Card Preview */}
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">
                    Gift Card Design
                  </h3>
                  {selectedDesign && (
                    <div className="bg-gray-100 rounded-lg p-6">
                      <div className="w-full">
                        <GiftCardWrapper
                          settings={selectedDesign}
                          businessLogoUrl={business.booking_settings?.logo_url}
                          showSampleData={true}
                          maxWidth="100%"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Right Column - Order Details */}
                <div className="space-y-6">
                  {/* Amount */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-bold text-gray-900">
                        Order Summary
                      </h3>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      {editingAmount ? (
                        <div className="space-y-3">
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-lg font-medium">
                              $
                            </span>
                            <input
                              type="tel"
                              inputMode="decimal"
                              pattern="[0-9]*"
                              value={amount}
                              onChange={(e) => {
                                // Only allow numbers and one decimal point
                                const value = e.target.value.replace(/[^0-9.]/g, '');
                                // Ensure only one decimal point
                                const parts = value.split('.');
                                const filteredValue = parts.length > 2 
                                  ? parts[0] + '.' + parts.slice(1).join('')
                                  : value;
                                setAmount(filteredValue);
                              }}
                              min="0.01"
                              step="0.01"
                              className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
                              placeholder="0.00"
                              autoFocus
                              style={{ 
                                WebkitAppearance: 'none',
                                MozAppearance: 'textfield',
                                touchAction: 'manipulation'
                              }}
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => {
                                if (amount && parseFloat(amount) > 0) {
                                  setEditingAmount(false);
                                }
                              }}
                              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              onClick={() => setEditingAmount(false)}
                              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center justify-between">
                            <span className="text-gray-700">Gift Card Amount</span>
                            <div className="flex items-center gap-3">
                              <span className="text-2xl font-bold text-gray-900">
                                ${parseFloat(amount || '0').toFixed(2)}
                              </span>
                              <button
                                type="button"
                                onClick={() => setEditingAmount(true)}
                                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                              >
                                Edit
                              </button>
                            </div>
                          </div>
                          {/* Show promotion bonus on confirmation screen */}
                          {promotionBonus && (
                            <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                              <div className="flex items-center gap-2 mb-2">
                                <Tag className="h-4 w-4 text-green-600" />
                                <span className="text-sm font-semibold text-green-900">
                                  {promotionBonus.promotion.name}
                                </span>
                                <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                                  ACTIVE
                                </span>
                              </div>
                              <div className="flex items-center justify-between text-sm">
                                <div className="text-gray-600">
                                  <div>Purchase: ${parseFloat(amount).toFixed(2)}</div>
                                  <div>Bonus: +${promotionBonus.bonusAmount.toFixed(2)}</div>
                                </div>
                                <div className="text-right">
                                  <div className="text-xs text-gray-600">Total Card Value</div>
                                  <div className="text-lg font-bold text-green-900">
                                    ${promotionBonus.totalAmount.toFixed(2)}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {/* Purchaser Information */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-lg font-semibold text-gray-900">
                        Purchaser Information
                      </h4>
                      {!editingPurchaser && (
                        <button
                          type="button"
                          onClick={() => setEditingPurchaser(true)}
                          className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                        >
                          Edit
                        </button>
                      )}
                    </div>
                    {editingPurchaser ? (
                      <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              First Name *
                            </label>
                            <input
                              type="text"
                              value={firstName}
                              onChange={(e) => setFirstName(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Last Name *
                            </label>
                            <input
                              type="text"
                              value={lastName}
                              onChange={(e) => setLastName(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              required
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Email *
                          </label>
                          <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Phone Number *
                          </label>
                          <input
                            type="tel"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            required
                          />
                        </div>
                        <div className="flex items-center gap-2 pt-2">
                          <button
                            type="button"
                            onClick={() => {
                              if (firstName && lastName && email && phone) {
                                setEditingPurchaser(false);
                              }
                            }}
                            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditingPurchaser(false)}
                            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                        <div>
                          <span className="text-sm text-gray-600">Name:</span>
                          <span className="ml-2 text-gray-900 font-medium">
                            {firstName} {lastName}
                          </span>
                        </div>
                        <div>
                          <span className="text-sm text-gray-600">Email:</span>
                          <span className="ml-2 text-gray-900 font-medium">
                            {email}
                          </span>
                        </div>
                        <div>
                          <span className="text-sm text-gray-600">Phone:</span>
                          <span className="ml-2 text-gray-900 font-medium">
                            {phone}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Recipient Information */}
                  {(showRecipient || recipientName || recipientEmail) && (
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-lg font-semibold text-gray-900">
                          Recipient Information
                        </h4>
                        {!editingRecipient && (
                          <button
                            type="button"
                            onClick={() => {
                              setEditingRecipient(true);
                              setShowRecipient(true);
                            }}
                            className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                          >
                            Edit
                          </button>
                        )}
                      </div>
                      {editingRecipient ? (
                        <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Recipient Name
                            </label>
                            <input
                              type="text"
                              value={recipientName}
                              onChange={(e) => setRecipientName(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              placeholder="Enter recipient name"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Recipient Email
                            </label>
                            <input
                              type="email"
                              value={recipientEmail}
                              onChange={(e) => setRecipientEmail(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              placeholder="Enter recipient email"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Custom Message
                            </label>
                            <textarea
                              value={recipientMessage}
                              onChange={(e) => setRecipientMessage(e.target.value)}
                              rows={3}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              placeholder="Enter a custom message for the recipient (optional)"
                            />
                          </div>
                          <div className="flex items-center gap-2 pt-2">
                            <button
                              type="button"
                              onClick={() => setEditingRecipient(false)}
                              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setEditingRecipient(false);
                                if (!recipientName && !recipientEmail) {
                                  setShowRecipient(false);
                                }
                              }}
                              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                          {recipientName ? (
                            <div>
                              <span className="text-sm text-gray-600">Name:</span>
                              <span className="ml-2 text-gray-900 font-medium">
                                {recipientName}
                              </span>
                            </div>
                          ) : null}
                          {recipientEmail ? (
                            <div>
                              <span className="text-sm text-gray-600">Email:</span>
                              <span className="ml-2 text-gray-900 font-medium">
                                {recipientEmail}
                              </span>
                            </div>
                          ) : null}
                          {recipientMessage ? (
                            <div>
                              <span className="text-sm text-gray-600">Message:</span>
                              <p className="mt-1 text-gray-900 font-medium">
                                {recipientMessage}
                              </p>
                            </div>
                          ) : null}
                          {!recipientName && !recipientEmail && !recipientMessage && (
                            <p className="text-sm text-gray-500 italic">No recipient information added</p>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Checkout Button */}
                  <div className="pt-6 border-t border-gray-200">
                    <div className="flex items-center justify-between gap-4">
                      <button
                        type="button"
                        onClick={() => setCurrentStep('purchaser')}
                        className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 active:bg-gray-100 font-medium transition-colors"
                        style={{
                          minHeight: '44px',
                          touchAction: 'manipulation',
                          WebkitTapHighlightColor: 'transparent'
                        }}
                      >
                        Back
                      </button>
                      <button
                        type="button"
                        onClick={handleCheckout}
                        disabled={!stripeActive || processingCheckout || !amount || !firstName || !lastName || !email || !phone}
                        className={`flex-1 px-8 py-4 rounded-lg font-semibold text-lg transition-colors flex items-center justify-center space-x-2 ${
                          stripeActive && !processingCheckout && amount && firstName && lastName && email && phone
                            ? 'bg-blue-600 hover:bg-blue-700 text-white'
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                      >
                        {processingCheckout ? (
                          <>
                            <Loader2 className="h-5 w-5 animate-spin" />
                            <span>Processing...</span>
                          </>
                        ) : !stripeActive ? (
                          <>
                            <AlertCircle className="h-5 w-5" />
                            <span>Checkout Unavailable</span>
                          </>
                        ) : (
                          <>
                            <CreditCard className="h-5 w-5" />
                            <span>Checkout</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

